import { useEffect, useMemo, useRef } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryFunctionContext,
} from '@tanstack/react-query';
import { useForm, type Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { useAuthStore } from '@/features/auth/auth.store';
import { useFormGuardStore } from '@/stores/form-guard.store';
import type { IBaseResponse } from '@/types/base-response';
import { PricePresetService } from '../price-preset.services';
import type {
  IAgeGroupPreset,
  IGroupSizePreset,
  IPresetFilters,
} from '../price-preset.types';
import {
  sortAgeGroupPresets,
  sortGroupSizePresets,
  type AgeGroupPresetValues,
  type GroupSizePresetValues,
} from '../price-preset.utils';
import {
  ageGroupPresetValidationSchema,
  groupSizePresetValidationSchema,
} from '../price-preset.validations';
import { getPageSize, type PageSizeKey } from '@/stores/page-size.store';
import { useUrlFilters, listPathWithFilters } from '@/hooks/use-url-filters';

const errorMessage = (err: unknown) =>
  (err as { message?: string } | undefined)?.message ?? 'Something went wrong';

const initialFilters: IPresetFilters = {
  search: '',
  page: 1,
  limit: 10,
  sort: '',
  order: '',
  status: 'active',
};

/**
 * A settings list's filters and query, shaped like the Smart Suggestion lists:
 * one status at a time (Suspended included, so a deleted preset can be found
 * and brought back) and a reset. No `companyId`: the API only honours it for a
 * super Admin and otherwise scopes to the caller's company.
 */
const usePresetList = <T>(
  key: string,
  queryFn: (context: QueryFunctionContext) => Promise<IBaseResponse<T[]>>,
  pageSizeKey: PageSizeKey
) => {
  const searchRef = useRef<HTMLInputElement | null>(null);
  const [filters, setFilters] = useUrlFilters<IPresetFilters>({
    ...initialFilters,
    limit: getPageSize(pageSizeKey),
  });

  const cleanFilters = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(filters).filter(
          ([, value]) => value !== '' && value !== undefined && value !== null
        )
      ),
    [filters]
  );

  const { data, isLoading } = useQuery({
    queryKey: [key, cleanFilters],
    queryFn,
  });

  const handleResetFilter = () => {
    if (searchRef.current) searchRef.current.value = '';
    setFilters({ ...initialFilters, limit: getPageSize(pageSizeKey) });
  };

  return { filters, setFilters, data, isLoading, searchRef, handleResetFilter };
};

const useAgeGroupPresetList = () =>
  usePresetList<IAgeGroupPreset>(
    'age-group-preset-list',
    PricePresetService.getAgeGroupPresetList,
    'setting.age-group-preset'
  );

const useGroupSizePresetList = () =>
  usePresetList<IGroupSizePreset>(
    'group-size-preset-list',
    PricePresetService.getGroupSizePresetList,
    'setting.group-size-preset'
  );

/** Every active preset, for the Pricing tab's pick dialogs. */
const PICKER_FILTERS = { status: 'active', page: 1, limit: 100 };

const usePricePresetOptions = () => {
  const ages = useQuery({
    queryKey: ['age-group-preset-options', PICKER_FILTERS],
    queryFn: PricePresetService.getAgeGroupPresetList,
    select: res => sortAgeGroupPresets(res?.data ?? []),
  });
  const groups = useQuery({
    queryKey: ['group-size-preset-options', PICKER_FILTERS],
    queryFn: PricePresetService.getGroupSizePresetList,
    select: res => sortGroupSizePresets(res?.data ?? []),
  });

  return {
    ageGroupPresets: ages.data ?? [],
    isLoadingAgeGroupPresets: ages.isPending,
    groupSizePresets: groups.data ?? [],
    isLoadingGroupSizePresets: groups.isPending,
  };
};

/**
 * The shared save/delete wiring of both preset forms. Delete is an edit to
 * `suspended` carrying the stored values, so a PUT that replaces the record
 * cannot blank the rest of it.
 */
const usePresetMutations = <Values extends { status?: string }>({
  id,
  stored,
  create,
  edit,
  deletedMessage,
  listRoute,
}: {
  id: string | undefined;
  /** The record as loaded, for Delete; undefined until it arrives. */
  stored: Values | undefined;
  create: (values: Values) => Promise<IBaseResponse<unknown>>;
  edit: (args: { id: string; data: Values }) => Promise<IBaseResponse<unknown>>;
  deletedMessage: string;
  /** Where a save lands: the tab's list, not "back", which a deep link breaks. */
  listRoute: string;
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setIgnoreGuard = useFormGuardStore(state => state.setIgnoreGuard);

  const createMutation = useMutation({ mutationFn: create });
  const editMutation = useMutation({ mutationFn: edit });

  const done = (message: string | undefined) => {
    setIgnoreGuard(true);
    queryClient.invalidateQueries();
    toast.success(message ?? 'Saved');
    navigate(listPathWithFilters(listRoute));
  };

  const onSubmit = (values: Values) => {
    const callbacks = {
      onSuccess: (res: IBaseResponse<unknown>) => done(res?.meta?.message),
      onError: (err: unknown) => toast.error(errorMessage(err)),
    };
    if (id) editMutation.mutate({ id, data: values }, callbacks);
    else createMutation.mutate(values, callbacks);
  };

  const handleDelete = () => {
    if (!id || !stored) return;
    editMutation.mutate(
      { id, data: { ...stored, status: 'suspended' } },
      {
        onSuccess: () => done(deletedMessage),
        onError: err => toast.error(errorMessage(err)),
      }
    );
  };

  return {
    createMutation,
    editMutation,
    onSubmit,
    handleDelete,
    isSaving: createMutation.isPending || editMutation.isPending,
  };
};

type AgeGroupPresetInput = {
  companyId: string;
  label: string;
  defaultAmount?: number | string | null;
  order?: number | string | null;
  status: string;
};

const useAgeGroupPresetForm = () => {
  const { id } = useParams();
  const companyId = useAuthStore(state => state.user?.company?.id ?? '');

  const form = useForm<AgeGroupPresetInput, unknown, AgeGroupPresetValues>({
    resolver: yupResolver(ageGroupPresetValidationSchema) as Resolver<
      AgeGroupPresetInput,
      unknown,
      AgeGroupPresetValues
    >,
    defaultValues: {
      companyId,
      label: '',
      defaultAmount: 0,
      order: '',
      status: 'active',
    },
    mode: 'onChange',
  });

  const { data, isLoading } = useQuery({
    queryKey: ['age-group-preset', id],
    queryFn: PricePresetService.getAgeGroupPresetById,
    enabled: !!id,
    select: res => res?.data,
  });

  const hydrated = useMemo<AgeGroupPresetInput | undefined>(
    () =>
      data && {
        companyId: data.companyId ?? companyId,
        label: data.label ?? '',
        defaultAmount: data.defaultAmount ?? 0,
        order: data.order ?? '',
        status: data.status ?? 'active',
      },
    [data, companyId]
  );

  useEffect(() => {
    if (hydrated) form.reset(hydrated);
  }, [hydrated, form]);

  const mutations = usePresetMutations<AgeGroupPresetValues>({
    id,
    stored: hydrated && ageGroupPresetValidationSchema.cast(hydrated),
    create: PricePresetService.createAgeGroupPreset,
    edit: PricePresetService.editAgeGroupPreset,
    deletedMessage: 'Age group deleted',
    listRoute: '/setting/company/age-groups',
  });

  return {
    id,
    form,
    isLoading,
    handleReset: () => hydrated && form.reset(hydrated),
    ...mutations,
  };
};

type GroupSizePresetInput = {
  companyId: string;
  label?: string | null;
  minParticipant?: number | string | null;
  maxParticipant?: number | string | null;
  defaultAmount?: number | string | null;
  status: string;
};

const useGroupSizePresetForm = () => {
  const { id } = useParams();
  const companyId = useAuthStore(state => state.user?.company?.id ?? '');

  const form = useForm<GroupSizePresetInput, unknown, GroupSizePresetValues>({
    resolver: yupResolver(groupSizePresetValidationSchema) as Resolver<
      GroupSizePresetInput,
      unknown,
      GroupSizePresetValues
    >,
    defaultValues: {
      companyId,
      label: '',
      minParticipant: '',
      maxParticipant: '',
      defaultAmount: 0,
      status: 'active',
    },
    mode: 'onChange',
  });

  const { data, isLoading } = useQuery({
    queryKey: ['group-size-preset', id],
    queryFn: PricePresetService.getGroupSizePresetById,
    enabled: !!id,
    select: res => res?.data,
  });

  const hydrated = useMemo<GroupSizePresetInput | undefined>(
    () =>
      data && {
        companyId: data.companyId ?? companyId,
        label: data.label ?? '',
        minParticipant: data.minParticipant ?? '',
        maxParticipant: data.maxParticipant ?? '',
        defaultAmount: data.defaultAmount ?? 0,
        status: data.status ?? 'active',
      },
    [data, companyId]
  );

  useEffect(() => {
    if (hydrated) form.reset(hydrated);
  }, [hydrated, form]);

  const mutations = usePresetMutations<GroupSizePresetValues>({
    id,
    stored: hydrated && groupSizePresetValidationSchema.cast(hydrated),
    create: PricePresetService.createGroupSizePreset,
    edit: PricePresetService.editGroupSizePreset,
    deletedMessage: 'Group size deleted',
    listRoute: '/setting/company/group-sizes',
  });

  return {
    id,
    form,
    isLoading,
    handleReset: () => hydrated && form.reset(hydrated),
    ...mutations,
  };
};

export {
  usePresetList,
  usePresetMutations,
  useAgeGroupPresetList,
  useGroupSizePresetList,
  usePricePresetOptions,
  useAgeGroupPresetForm,
  useGroupSizePresetForm,
};
